/**
 * Control API client.
 *
 * Everything goes through here rather than scattered fetch() calls, so the
 * two rules that matter are enforced in one place: credentials are always
 * included (the session cookie is HttpOnly, so the browser must attach it —
 * JavaScript cannot), and a 401 is surfaced as a distinct type rather than a
 * generic failure, because the UI has to react to it by signing out rather
 * than showing an error toast.
 */

export type Role =
  | "viewer"
  | "analyst"
  | "strategy_manager"
  | "risk_manager"
  | "operator"
  | "owner";

export interface User {
  user_id: string;
  username: string;
  role: Role;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface Session {
  user: User;
  permissions: string[];
}

export interface AccountOverview {
  account_ref: string;
  environment: string;
  account: Record<string, string> | null;
  positions: Array<Record<string, unknown>>;
  last_report_at: string | null;
  last_heartbeat_at: string | null;
  ea_mode: string | null;
}

export interface Overview {
  accounts: AccountOverview[];
  live_trading_hard_disabled: boolean;
  server_time: string;
}

export interface CommandRow {
  sequence: number;
  command_id: string;
  account_ref: string;
  environment: string;
  state: string;
  action: string | null;
  provider_symbol: string | null;
  side: string | null;
  volume: string | null;
  created_at: string;
  expires_at: string;
  updated_at: string;
  delivered_to_ea_instance_id: string | null;
  acknowledged_at: string | null;
  result: Record<string, unknown> | null;
}

export interface AuditRow {
  event_id: string;
  created_at: string;
  actor: string;
  action: string;
  resource: string;
  reason: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  trace_id: string;
}

export interface MarketCandle {
  open_time: string;
  open: string;
  high: string;
  low: string;
  close: string;
  tick_volume: number;
}

export interface MarketSymbol {
  provider_symbol: string;
  // Null until the first heartbeat carrying a quote arrives — bars can be
  // stored before that, and the page says so rather than showing a fake price.
  bid: string | null;
  ask: string | null;
  spread_points: number | null;
  // The PRICE's own time (MT5 SYMBOL_TIME) and its age. Null when the EA
  // build does not report it — the age is then unknown, and the page says so
  // rather than borrowing quote_sent_at, which would show "2s ago" next to a
  // price frozen since Friday.
  quote_time: string | null;
  quote_age_seconds: number | null;
  // When the terminal last SENT a quote: liveness of the EA, not freshness of
  // the price.
  quote_sent_at: string | null;
  candle_count: number;
  last_candle_open_time: string | null;
  gap_count: number;
  candles: MarketCandle[];
}

export interface Market {
  timeframe: string;
  symbols: MarketSymbol[];
  server_time: string;
}

export interface Config {
  allowed_ea_modes: string[];
  command_poll_limit: number;
  ack_result_timeout_seconds: number;
  risk_policy_path: string | null;
  instrument_registry_path: string | null;
  editable_here: boolean;
  editable_here_reason: string;
}

/** One kill-switch trigger that fired and has not been cleared. */
export interface KillSwitchLatch {
  trigger_code: string;
  level: number;
  first_latched_at: string;
  last_observed_at: string;
  detail: string;
  // Only present on the history endpoint.
  cleared_at?: string | null;
  cleared_by?: string | null;
  cleared_reason?: string | null;
}

export interface KillSwitchAccount {
  account_ref: string;
  level: number;
  level_name: string;
  engaged_triggers: string[];
  manual_level: number;
  manual_level_name: string;
  manual_reason: string | null;
  halted_strategies: string[];
  // The three questions the rest of the system asks, answered by the server
  // so the UI never re-derives them from the level and gets it wrong.
  signals_allowed: boolean;
  new_entries_allowed: boolean;
  execution_allowed: boolean;
  latches: KillSwitchLatch[];
}

export interface KillSwitch {
  accounts: KillSwitchAccount[];
  levels: Array<{ level: number; name: string }>;
  server_time: string;
}

/** A validated agent result (schemas/agent-envelope.schema.json). Every field
 * except `output`, `confidence`, `limitations` and `requires_human_review` was
 * filled in by the server, not by the model. */
export interface AgentEnvelope {
  trace_id: string;
  agent: string;
  model: string;
  prompt_version: string;
  input_refs: string[];
  tool_calls: Array<{ tool: string; ok: boolean; error: string | null }>;
  output: Record<string, unknown>;
  confidence: "low" | "medium" | "high";
  limitations: string[];
  requires_human_review: boolean;
  created_at: string;
}

/** One entry in the docs/02-agents.md §2 catalog, built or not. */
export interface AgentEntry {
  name: string;
  catalog_ref: string;
  title: string;
  purpose: string;
  authority: "EXPLAINS" | "PRODUCES_EVIDENCE" | "PROPOSES";
  tools: string[];
  implemented: boolean;
  prompt_version: string | null;
  notes: string[];
  paused: boolean;
  paused_by: string | null;
  paused_reason: string | null;
  runs: number;
  failed: number;
  needs_review: number;
  last_run_at: string | null;
  last_latency_ms: number | null;
  last_model: string | null;
}

export interface AgentRun {
  run_id: string;
  agent: string;
  model: string;
  prompt_version: string;
  trace_id: string;
  requested_by: string;
  started_at: string;
  latency_ms: number;
  ok: boolean;
  confidence: string;
  requires_human_review: boolean;
  input_refs: string[];
  output: Record<string, unknown>;
  limitations: string[];
  error: string | null;
}

const BASE = "/api/v1/control";

/** The session is gone or was never valid. The UI signs out rather than
 * retrying, since no amount of retrying will fix it. */
export class NotAuthenticatedError extends Error {}

/** Authenticated, but the role does not hold the required permission.
 * Distinct from NotAuthenticatedError: signing out would not help. */
export class PermissionDeniedError extends Error {}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    // The session cookie is HttpOnly; without this the browser will not
    // attach it and every request looks unauthenticated.
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
  });

  if (response.ok) {
    return (await response.json()) as T;
  }

  // Every error response follows schemas/error.schema.json, but a proxy or
  // a crash could still return non-JSON — fall back rather than throwing a
  // parse error that hides the real status.
  let code = "UNKNOWN";
  let message = `Request failed (HTTP ${response.status})`;
  try {
    const body = await response.json();
    code = body.error_code ?? code;
    message = body.message_safe ?? message;
  } catch {
    /* keep the fallback */
  }

  if (response.status === 401) throw new NotAuthenticatedError(message);
  if (response.status === 403) throw new PermissionDeniedError(message);
  throw new ApiError(message, code, response.status);
}

export type CalendarEventView = {
  title: string;
  currency: string;
  impact: string;
  scheduled_at: string;
  forecast: string;
  previous: string;
};

export type CalendarStatus = {
  state: {
    fetched_at: string;
    age_hours: number;
    covers_until: string | null;
    event_count: number;
    last_error: string | null;
    last_error_at: string | null;
  } | null;
  staleness_problem: string | null;
  max_age_hours: number;
  refresh_interval_hours: number;
  upcoming: CalendarEventView[];
  server_time: string;
};

export type PromotionApproval = {
  approver: string;
  approved_at: string;
  reason: string;
  applies_to_current_content: boolean;
};

export type Promotion = {
  request_id: string;
  kind: string;
  target_version: string;
  state: string;
  content_hash: string;
  proposed_by: string;
  proposed_at: string;
  reason: string;
  evidence_refs: string[];
  approvals: PromotionApproval[];
  approvals_valid: number;
  approvals_required: number;
  can_activate: boolean;
  activated_at: string | null;
  updated_at: string;
};

export type PromotionList = {
  promotions: Promotion[];
  kinds: Array<{ kind: string; required_approvals: number }>;
};

export type AgentProposalResult = {
  proposed: boolean;
  declined_reason: string | null;
  agent_run_id: string;
  review: {
    agent: string;
    model: string;
    prompt_version: string;
    confidence: string;
    requires_human_review: boolean;
    output: Record<string, unknown>;
    limitations: string[];
  };
  promotion: Promotion | null;
  trace_id: string;
};

export const api = {
  login: (username: string, password: string) =>
    request<Session & { expires_at: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<{ status: string }>("/auth/logout", { method: "POST" }),

  me: () => request<Session & { session_expires_at: string }>("/auth/me"),

  overview: () => request<Overview>("/overview"),

  commands: (limit = 50) =>
    request<{ commands: CommandRow[] }>(`/commands?limit=${limit}`),

  audit: (limit = 100) => request<{ events: AuditRow[] }>(`/audit?limit=${limit}`),

  config: () => request<Config>("/config"),

  market: (timeframe = "M5", candles = 120) =>
    request<Market>(`/market?timeframe=${timeframe}&candles=${candles}`),

  users: () => request<{ users: User[] }>("/users"),

  createUser: (username: string, password: string, role: Role) =>
    request<{ user: User }>("/users", {
      method: "POST",
      body: JSON.stringify({ username, password, role }),
    }),

  updateUser: (userId: string, changes: { role?: Role; is_active?: boolean }) =>
    request<{ user: User }>(`/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    }),

  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    request<{ status: string; other_sessions_revoked: number }>("/auth/password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),

  killSwitch: () => request<KillSwitch>("/kill-switch"),

  calendar: () => request<CalendarStatus>("/calendar"),

  promotions: () => request<PromotionList>("/promotions"),

  proposeViaAgent: (body: {
    kind: string;
    target_version: string;
    reason: string;
    content: Record<string, unknown>;
  }) =>
    request<AgentProposalResult>("/promotions/agent-proposal", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  approvePromotion: (requestId: string, reason: string) =>
    request<Promotion>(`/promotions/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  rejectPromotion: (requestId: string, reason: string) =>
    request<Promotion>(`/promotions/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  activatePromotion: (requestId: string) =>
    request<Promotion>(`/promotions/${requestId}/activate`, { method: "POST" }),

  rollbackPromotion: (requestId: string, reason: string) =>
    request<Promotion>(`/promotions/${requestId}/rollback`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  agents: () => request<{ agents: AgentEntry[]; server_time: string }>("/agents"),

  agentRuns: (reviewOnly = false, limit = 50) =>
    request<{ runs: AgentRun[] }>(
      `/agents/runs?review_only=${reviewOnly}&limit=${limit}`,
    ),

  pauseAgent: (agent: string, paused: boolean, reason: string) =>
    request<{ agent: string; paused: boolean; was_paused?: boolean }>(
      `/agents/${agent}/pause`,
      { method: "POST", body: JSON.stringify({ paused, reason }) },
    ),

  /** Read-only. The agent explains recorded decisions; it holds no tool that
   * writes, so nothing it returns can cause anything to happen. */
  explain: (providerSymbol: string, question: string) =>
    request<AgentEnvelope>("/explain", {
      method: "POST",
      body: JSON.stringify({ provider_symbol: providerSymbol, question }),
    }),

  engageKillSwitch: (
    accountRef: string,
    level: number,
    reason: string,
    haltedStrategies: string[] = [],
  ) =>
    request<KillSwitchAccount>(`/kill-switch/${accountRef}/engage`, {
      method: "POST",
      body: JSON.stringify({
        level,
        reason,
        halted_strategies: haltedStrategies,
      }),
    }),

  /** Releases ONE trigger. There is deliberately no bulk-clear call. */
  clearKillSwitchTrigger: (accountRef: string, triggerCode: string, reason: string) =>
    request<KillSwitchAccount & { released: boolean }>(
      `/kill-switch/${accountRef}/clear`,
      {
        method: "POST",
        body: JSON.stringify({ trigger_code: triggerCode, reason }),
      },
    ),

  killSwitchHistory: (accountRef: string, limit = 50) =>
    request<{ account_ref: string; latches: KillSwitchLatch[] }>(
      `/kill-switch/${accountRef}/history?limit=${limit}`,
    ),

  resetUserPassword: (userId: string, newPassword: string) =>
    request<{ status: string; sessions_revoked: number }>(`/users/${userId}/password`, {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    }),
};
